import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { V, Haptic } from '../../utils/theme';
import { LS } from '../../utils/storage';
import { Icons } from '../../components/Icons';
import { track, SectionTitle, EmptyState, onAccent } from '../../components/kit';
import { Card, Btn, Field, Sheet, Chip, Stat, Progress, Skeleton, SkeletonCard, SuccessToastCtrl, ConfirmCtrl, MsgBannerCtrl } from '../../components/ui';
import { today, ago, fmtShort, fmtFull, uid, calc1RM, calcPlates, PLATES, convW, wUnit, dUnit, isCardio, toKg, toLbs, friendDisplayName, chartCfg } from '../../utils/helpers';
import { CloudSync, SocialAPI, SyncQueue, SYNC_URL, APP_VERSION } from '../../utils/sync';
import { apiPost } from '../../utils/api';
import { Undo } from '../../utils/undo';
import { useLayout } from '../../utils/responsive';
import { ShareCard } from '../../utils/share';
import { appName } from '../../utils/brand';
import { flagOn } from '../../utils/org';
import { BADGE_DEFS, calcEarnedBadges } from '../../data/badges';
import { TEMPLATES } from '../../data/templates';
import { IRON_RANKS } from '../../data/ranks';
import { FF_HACKS, FF_BRANDS } from '../../data/hacks';
import { FOODS } from '../../data/foods';
import { AuthToken } from '../../utils/auth';
import { calcReadiness, useStreak, usePRs, useNutritionStreak } from '../../components/dialogs';
import { normalizePhasesFromWire, phaseIsActive } from '../../utils/healthWire';

export function WeeklySummary({s}){
  const { isDesktop } = useLayout();
  const canvasRef=useRef(null);
  const [generated,setGenerated]=useState(false);

  const stats=useMemo(()=>{
    const ws=s.workouts.filter(w=>w.date>=ago(7));
    const ns=s.nutrition.filter(n=>n.date>=ago(7));
    const r=calcReadiness(s);
    const totalVol=ws.reduce((v,w)=>v+w.exercises.reduce((e,ex)=>e+ex.sets.reduce((ss,st)=>ss+(parseFloat(st.weight)||0)*(parseInt(st.reps)||0),0),0),0);
    const avgCal=ns.length?Math.round(ns.reduce((a,n)=>a+n.cal,0)/ns.length):0;
    const avgProt=ns.length?Math.round(ns.reduce((a,n)=>a+n.protein,0)/ns.length):0;
    const protHit=ns.filter(n=>n.protein>=s.goals.protein*0.8).length;
    const avgSleep=ns.filter(n=>parseFloat(n.sleep)>0).length?
      (ns.filter(n=>parseFloat(n.sleep)>0).reduce((a,n)=>a+(parseFloat(n.sleep)||0),0)/ns.filter(n=>parseFloat(n.sleep)>0).length).toFixed(1):0;
    const activePhase=normalizePhasesFromWire(s.phases||[]).find(p=>phaseIsActive(p));
    return{workouts:ws.length,totalVol,avgCal,avgProt,protHit,avgSleep,readiness:r.score,readinessLevel:r.level,
      readinessColor:r.color,phase:activePhase?.type||null};
  },[s]);

  const gen=()=>{
    const c=canvasRef.current;if(!c)return;
    const ctx=c.getContext("2d");c.width=600;c.height=440;
    const grad=ctx.createLinearGradient(0,0,600,440);
    grad.addColorStop(0,"#0a0a14");grad.addColorStop(1,"#14142a");
    ctx.fillStyle=grad;ctx.fillRect(0,0,600,440);
    ctx.fillStyle="#22d3ee";ctx.fillRect(0,0,4,440);

    ctx.fillStyle="#fff";ctx.font="bold 24px -apple-system, sans-serif";
    ctx.fillText("WEEKLY REVIEW",24,40);
    ctx.fillStyle="#666";ctx.font="13px -apple-system, sans-serif";
    const end=new Date();const start=new Date();start.setDate(start.getDate()-7);
    ctx.fillText(`${start.toLocaleDateString("en-US",{month:"short",day:"numeric"})} - ${end.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}`,24,62);

    if(stats.phase){ctx.fillStyle="#a78bfa";ctx.font="bold 11px -apple-system, sans-serif";ctx.fillText(`PHASE: ${stats.phase.toUpperCase()}`,420,40);}

    const rows=[
      {l:"WORKOUTS",v:String(stats.workouts),sub:"/7 days"},
      {l:"VOLUME",v:stats.totalVol>1000?`${(stats.totalVol/1000).toFixed(1)}k`:""+stats.totalVol,sub:`total ${wUnit(s.units)}`},
      {l:"AVG CALORIES",v:String(stats.avgCal),sub:"kcal/day"},
      {l:"AVG PROTEIN",v:`${stats.avgProt}g`,sub:`hit ${stats.protHit}/7 days`},
      {l:"AVG SLEEP",v:`${stats.avgSleep}h`,sub:"per night"},
      {l:"READINESS",v:String(stats.readiness),sub:stats.readinessLevel},
    ];
    rows.forEach((r,i)=>{
      const y=100+i*52;
      ctx.fillStyle="#444";ctx.fillRect(24,y+35,552,1);
      ctx.fillStyle="#888";ctx.font="10px -apple-system, sans-serif";ctx.fillText(r.l,24,y+12);
      ctx.fillStyle="#22d3ee";ctx.font="bold 24px -apple-system, sans-serif";ctx.fillText(r.v,24,y+34);
      ctx.fillStyle="#555";ctx.font="12px -apple-system, sans-serif";ctx.fillText(r.sub,180,y+34);
    });

    ctx.fillStyle="#333";ctx.font="bold 11px -apple-system, sans-serif";ctx.fillText(appName(),24,425);
    ctx.fillStyle="#555";ctx.font="10px -apple-system, sans-serif";ctx.fillText("ironlog.space",100,425);
    setGenerated(true);
  };

  const share=()=>{
    const c=canvasRef.current;if(!c)return;
    c.toBlob(blob=>{
      if(navigator.share&&blob){
        navigator.share({files:[new File([blob],"weekly-review.png",{type:"image/png"})],title:"Weekly Review"}).catch(()=>{});
      }else{
        const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download=`review-${today()}.png`;a.click();URL.revokeObjectURL(url);
      }
    },"image/png");
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Weekly Summary</div>

      <Card style={{padding:14}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
          {[{l:"Workouts",v:stats.workouts,c:V.accent},{l:"Avg Cal",v:stats.avgCal,c:V.warn},{l:"Readiness",v:stats.readiness,c:stats.readinessColor},
            {l:"Protein Hit",v:`${stats.protHit}/7`,c:V.accent2},{l:"Avg Sleep",v:`${stats.avgSleep}h`,c:V.purple},{l:"Volume",v:stats.totalVol>1000?`${(stats.totalVol/1000).toFixed(1)}k`:stats.totalVol,c:V.accent}
          ].map(m=>(
            <div key={m.l} style={{textAlign:"center",padding:"10px 0",borderRadius:8,background:V.card}}>
              <div style={{fontSize:18,fontWeight:800,color:m.c,fontFamily:V.mono}}>{m.v}</div>
              <div style={{fontSize:11,color:V.text3,fontWeight:600}}>{m.l}</div>
            </div>
          ))}
        </div>
      </Card>

      <canvas ref={canvasRef} style={{width:"100%",borderRadius:12,display:generated?"block":"none"}}/>
      {!generated?(
        <Btn full onClick={gen}>{Icons.chart({size:14,color:onAccent()})} Generate Report Card</Btn>
      ):(
        <div style={{display:"flex",gap:8}}>
          {flagOn("share_cards")&&<Btn full onClick={share}>{Icons.upload({size:14,color:onAccent()})} Share</Btn>}
          <Btn v="secondary" full onClick={()=>setGenerated(false)}>Close</Btn>
        </div>
      )}
    </div>
  );
}

export function PersonalRecords({s,d}){
  const { isDesktop } = useLayout();
  const prs=useMemo(()=>{
    const map={};
    s.workouts.forEach(w=>w.exercises.forEach(ex=>{
      const name=s.exercises.find(e=>e.id===ex.exerciseId)?.name||ex.exerciseId;
      const cat=s.exercises.find(e=>e.id===ex.exerciseId)?.cat||"Other";
      ex.sets.forEach(st=>{
        const wt=parseFloat(st.weight)||0;const reps=parseInt(st.reps)||0;
        if(wt<=0)return;
        const e1rm=calc1RM(wt,reps);
        if(!map[ex.exerciseId]||e1rm>map[ex.exerciseId].e1rm){
          map[ex.exerciseId]={name,cat,weight:wt,reps,e1rm,date:w.date,exerciseId:ex.exerciseId};
        }
      });
    }));
    return Object.values(map).sort((a,b)=>b.e1rm-a.e1rm);
  },[s.workouts,s.exercises]);

  // Find how close recent lifts are to PR
  const recentPct=(exerciseId)=>{
    const pr=prs.find(p=>p.exerciseId===exerciseId);if(!pr)return null;
    for(const w of s.workouts.slice(0,10)){
      const ex=(w.exercises||[]).find(e=>e.exerciseId===exerciseId);
      if(ex&&ex.sets?.length){const best=Math.max(...ex.sets.map(st=>calc1RM(parseFloat(st.weight)||0,parseInt(st.reps)||0)));
        if(best>0)return Math.round(best/pr.e1rm*100);}
    }return null;
  };

  const [catFilter,setCatFilter]=useState("All");
  const cats=["All",...new Set(prs.map(p=>p.cat))];
  const filtered=catFilter==="All"?prs:prs.filter(p=>p.cat===catFilter);
  const big3=["bench","squat","deadlift"].map(id=>prs.find(p=>p.exerciseId===id)).filter(Boolean);
  const big3Total=big3.reduce((a,p)=>a+p.weight,0);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Personal Records</div>
      {big3.length>0&&(
        <Card style={{padding:14,border:`1px solid ${V.warn}20`,background:`${V.warn}04`}}>
          <SectionTitle icon={Icons.trophy} color={V.warn} size={12} style={{marginBottom:8}}>Big 3 total: {big3Total} {wUnit(s.units)}</SectionTitle>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
            {big3.map(p=>(
              <div key={p.exerciseId} style={{textAlign:"center"}}>
                <div style={{fontSize:18,fontWeight:800,color:V.text,fontFamily:V.mono}}>{p.weight}</div>
                <div style={{fontSize:11,color:V.text3}}>{p.name.split(" ")[0]}</div>
                <div style={{fontSize:11,color:V.text3}}>{fmtShort(p.date)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
      <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
        {cats.map(c=><Chip key={c} label={c} active={catFilter===c} onClick={()=>setCatFilter(c)}/>)}
      </div>
      <div style={{fontSize:11,color:V.text3}}>{filtered.length} exercises with PRs</div>
      {filtered.map((pr,i)=>{
        const pct=recentPct(pr.exerciseId);
        return(
          <Card key={i} style={{padding:12,cursor:"pointer"}} onClick={()=>{
              // Open the per-exercise chart pre-selecting this lift
              LS.set("ft-sel-exercise",pr.exerciseId);
              if(d)d({type:"TAB",tab:"track_exercise_chart"});
            }}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:V.text}}>{pr.name}</div>
                <div style={{display:"flex",gap:6,alignItems:"center",marginTop:2}}>
                  <div style={{fontSize:11,color:V.text3}}>{pr.cat} · {fmtShort(pr.date)}</div>
                  {(()=>{const days=Math.round((Date.now()-new Date(pr.date+"T12:00:00"))/86400000);
                    const hot=days<=14;const cold=days>=180;
                    return <span style={{fontSize:11,fontWeight:700,padding:"1px 5px",borderRadius:4,
                      background:hot?"rgba(34,197,94,0.12)":cold?"rgba(56,189,248,0.1)":V.inputBg,
                      color:hot?"#22c55e":cold?"#38bdf8":V.text3}}>
                      {hot?"Recent":cold?"Cold":days<90?`${days}d ago`:`${Math.floor(days/30)}mo ago`}
                    </span>;
                  })()}
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:18,fontWeight:800,color:V.accent,fontFamily:V.mono}}>{pr.weight}×{pr.reps}</div>
                <div style={{fontSize:11,color:V.text3}}>E1RM: {pr.e1rm} {wUnit(s.units)}</div>
              </div>
            </div>
            {pct!==null&&pct<100&&(
              <div style={{marginTop:6}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:V.text3,marginBottom:2}}>
                  <span>Recent: {pct}% of PR</span>
                  <span>{100-pct}% to match</span>
                </div>
                <div style={{height:3,borderRadius:2,background:V.hoverBg}}>
                  <div style={{height:"100%",borderRadius:2,background:pct>=90?V.accent:pct>=75?V.warn:V.danger,width:`${pct}%`}}/>
                </div>
              </div>
            )}
            {pct===100&&<div style={{fontSize:11,color:V.accent,fontWeight:600,marginTop:4}}>Matched this session</div>}
          </Card>
        );
      })}
      {prs.length===0&&<Card style={{padding:20,textAlign:"center"}}><div style={{fontSize:12,color:V.text3}}>Log some workouts to see your PRs here</div></Card>}
    </div>
  );
}

export function SyncStatus({s}){
  const lastSync=LS.get("ft-last-sync");
  const email=s.profile?.email;
  const [pending,setPending]=useState(parseInt(LS.get("ft-pending-sync"))||0);
  const [retrying,setRetrying]=useState(false);

  // Listen for sync completion from SW
  useEffect(()=>{
    const handler=(e)=>{
      if(e.data?.type==="SYNC_COMPLETE"){setPending(e.data.remaining||0);LS.set("ft-pending-sync",e.data.remaining||0);}
      if(e.data?.type==="QUEUE_COUNT"){setPending(e.data.count||0);}
    };
    navigator.serviceWorker?.addEventListener("message",handler);
    // Ask SW for current queue count
    navigator.serviceWorker?.controller?.postMessage({type:"GET_QUEUE_COUNT"});
    return()=>navigator.serviceWorker?.removeEventListener("message",handler);
  },[]);

  // Update from localStorage on interval — pause when the tab is hidden so the
  // app doesn't tick continuously in a background tab (battery drain).
  useEffect(()=>{
    let iv=null;
    const start=()=>{ if(iv)return; iv=setInterval(()=>setPending(parseInt(LS.get("ft-pending-sync"))||0),5000); };
    const stop=()=>{ if(iv){clearInterval(iv);iv=null;} };
    const visHandler=()=>{ document.visibilityState==="visible"?start():stop(); };
    visHandler();
    document.addEventListener("visibilitychange",visHandler);
    return()=>{ stop(); document.removeEventListener("visibilitychange",visHandler); };
  },[]);

  const retry=async()=>{
    setRetrying(true);
    const result=await SyncQueue.processAll();
    setPending(result.remaining);
    setRetrying(false);
  };

  const fmtAgo=(iso)=>{
    if(!iso)return"Never";
    const sec=Math.floor((Date.now()-new Date(iso).getTime())/1000);
    if(sec<60)return"Just now";
    if(sec<3600)return`${Math.floor(sec/60)}m ago`;
    if(sec<86400)return`${Math.floor(sec/3600)}h ago`;
    return`${Math.floor(sec/86400)}d ago`;
  };

  if(!email)return null;

  return(
    <div style={{display:"flex",alignItems:"center",gap:6}}>
      <div style={{display:"flex",alignItems:"center",gap:4,padding:"4px 8px",
        background:pending>0?"rgba(255,159,67,0.06)":V.card,
        borderRadius:6,border:`1px solid ${pending>0?"rgba(255,159,67,0.15)":V.cardBorder}`}}>
        <div style={{width:5,height:5,borderRadius:3,background:pending>0?V.warn:lastSync?V.accent:track()}}/>
        <span style={{fontSize:11,color:pending>0?V.warn:V.text3,fontWeight:600}}>
          {pending>0?`${pending} pending`:lastSync?`${fmtAgo(lastSync)}`:"No sync"}
        </span>
      </div>
      {pending>0&&(
        <button onClick={retry} disabled={retrying} style={{padding:"3px 6px",borderRadius:4,background:`${V.warn}12`,
          border:"none",cursor:"pointer",fontSize:11,color:V.warn,fontWeight:700}}>
          {retrying?"…":"Retry"}
        </button>
      )}
    </div>
  );
}

// ─── #8 Social: Workout Proof Card Generator ───
